// Plik: frontend/src/pages/admin/Statistics.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, Card, CardContent, 
  CircularProgress, Alert, Button, Divider
} from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { adminService } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { parseISO, format } from 'date-fns';
import plLocale from 'date-fns/locale/pl';

// Kolory dla wykresów
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Statistics = () => {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previousVisitData, setPreviousVisitData] = useState([]);
  
  // Pobierz statystyki
  useEffect(() => {
    const fetchStatistics = async () => {
      setLoading(true);
      
      const response = await adminService.getStatistics();
      
      if (response.success) {
        setStatistics(response.data);
        
        // Pobierz dodatkowe dane o wcześniejszych wizytach
        const participantsResponse = await adminService.getParticipants();
        if (participantsResponse.success) {
          // Agreguj dane o wcześniejszych wizytach
          const previousVisitCounts = participantsResponse.data.reduce((acc, participant) => {
            const visit = participant.previous_visit || 'nie_podano';
            acc[visit] = (acc[visit] || 0) + 1;
            return acc;
          }, {});
          
          // Konwertuj do formatu dla wykresu
          const previousVisitArray = Object.entries(previousVisitCounts).map(([key, value]) => {
            let label;
            switch(key) {
              case 'nigdy':
                label = 'Nigdy';
                break;
              case 'rok_temu':
                label = 'Rok temu';
                break;
              case 'dwa_lata_temu':
                label = 'Dwa lata temu';
                break;
              case 'trzy_lub_wiecej_lat_temu':
                label = 'Trzy lub więcej lat temu';
                break;
              default:
                label = 'Nie podano';
            }
            return { name: label, value };
          });
          
          setPreviousVisitData(previousVisitArray);
        }
      } else {
        setError(response.error);
      }
      
      setLoading(false);
    };
    
    fetchStatistics();
  }, []);
  
  // Formatuj datę dla osi X
  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';
    const date = parseISO(dateStr);
    return format(date, 'EEE, d MMM', { locale: plLocale });
  };
  
  // Renderuj kartę podsumowania
  const renderSummaryCard = () => {
    if (!statistics.length) return null;
    
    const totalRegistered = statistics.reduce((total, stat) => total + stat.registered_count, 0);
    const totalPresent = statistics.reduce((total, stat) => total + stat.present_count, 0);
    const totalCompanions = statistics.reduce((total, stat) => total + stat.companions_count, 0);
    const totalPaid = statistics.reduce((total, stat) => total + stat.paid_count, 0);
    
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Podsumowanie
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="subtitle1" color="text.secondary">
                  Łącznie zapisanych
                </Typography>
                <Typography variant="h4">
                  {totalRegistered}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="subtitle1" color="text.secondary">
                  Obecnych
                </Typography>
                <Typography variant="h4">
                  {totalPresent}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="subtitle1" color="text.secondary">
                  Osoby towarzyszące
                </Typography>
                <Typography variant="h4">
                  {totalCompanions}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Box>
                <Typography variant="subtitle1" color="text.secondary">
                  Płatni uczestnicy
                </Typography>
                <Typography variant="h4">
                  {totalPaid}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };
  
  // Renderuj wykres rejestracji
  const renderRegistrationChart = () => {
    if (!statistics.length) return null;
    
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Liczba zapisów według dni
          </Typography>
          
          <Box sx={{ height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={statistics}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxis} 
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, 'Liczba']}
                  labelFormatter={(label) => `Data: ${formatXAxis(label)}`}
                />
                <Legend />
                <Bar 
                  name="Zapisani" 
                  dataKey="registered_count" 
                  fill={COLORS[0]} 
                />
                <Bar 
                  name="Obecni" 
                  dataKey="present_count" 
                  fill={COLORS[1]} 
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  // Renderuj wykres osób towarzyszących
  const renderCompanionsChart = () => {
    if (!statistics.length) return null;
    
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Osoby towarzyszące i płatni uczestnicy
          </Typography>
          
          <Box sx={{ height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={statistics}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatXAxis} 
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, 'Liczba']}
                  labelFormatter={(label) => `Data: ${formatXAxis(label)}`}
                />
                <Legend />
                <Bar 
                  name="Osoby towarzyszące" 
                  dataKey="companions_count" 
                  fill={COLORS[2]} 
                />
                <Bar 
                  name="Płatni uczestnicy" 
                  dataKey="paid_count" 
                  fill={COLORS[3]} 
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  // Renderuj wykres wcześniejszych wizyt
  const renderPreviousVisitsChart = () => {
    if (!previousVisitData.length) return null;
    
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Wcześniejsze wizyty
          </Typography>
          
          <Box sx={{ height: 300, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={previousVisitData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {previousVisitData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  // Przycisk eksportu danych
  const handleExport = () => {
    adminService.exportData();
  };
  
  if (loading) {
    return (
      <AdminLayout title="Statystyki">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }
  
  if (error) {
    return (
      <AdminLayout title="Statystyki">
        <Alert severity="error" sx={{ my: 3 }}>
          {error}
        </Alert>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout title="Statystyki">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Statystyki
        </Typography>
        
        <Button 
          variant="outlined"
          onClick={handleExport}
        >
          Eksportuj wszystkie dane
        </Button>
      </Box>
      
      {renderSummaryCard()}
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          {renderRegistrationChart()}
        </Grid>
        
        <Grid item xs={12} md={6}>
          {renderCompanionsChart()}
        </Grid>
        
        <Grid item xs={12} md={6}>
          {renderPreviousVisitsChart()}
        </Grid>
      </Grid>
    </AdminLayout>
  );
};

export default Statistics;